import { Input, UncontrolledTooltip } from "reactstrap";

import styles from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter.module.css";
import { useEffect, useState } from "react";
import { IColumnFilters } from "Pages/Run/Run";

const statusOptions = [
    "Abandoned",
    "Complete",
    "Errored",
    "Warning",
    "Debugging",
    "Started",
];

export default function RunColumnFilter({
    column,
    onSetColumnFilters,
}: {
    readonly column: any;
    readonly onSetColumnFilters: (filters: Partial<IColumnFilters>) => void;
}) {
    const columnName: keyof IColumnFilters = column.id;

    // DRAFT vs APPLIED, ported from Portal2 (Pages/Htmx/Review.cshtml — the no-name
    // visible inputs plus the hidden data-applied ones). `term` is what the user is
    // typing and only drives the input; `applied` is what the parent — and therefore
    // the back-end query — actually sees. Text filters commit on Enter.
    //
    // WHY. Previously every keystroke flowed to onSetColumnFilters (debounced 300 ms in
    // Run.tsx), and columnFilters is a dependency of SearchBox's fetch effect, so a
    // part-typed term fired its own search. Typing an ID like "8260050" issued a query
    // for "8", "82", "826"… — and a short, unselective prefix is exactly the expensive
    // case: an unindexed `name LIKE '%term%'` over every year-partition
    // (WebApps/Portal/docs/perf-run-polling-prod-bulley.md §4a). Committing on Enter
    // makes that one query instead of several, and keeps the 3 s/1 s auto-poll from
    // re-rendering the table with a half-typed filter.
    const [term, setTerm] = useState<string>("");
    const [applied, setApplied] = useState<string>("");

    // Sets columnFilters parent that holds all filters and will be sent
    // to SearchBox.tsx for back end fetch filter
    useEffect(() => {
        const lastFilter = localStorage.getItem(`runFilter.${columnName}`);
        if (lastFilter) {
            setTerm(lastFilter);
            setApplied(lastFilter);
            const newFilter = { [columnName]: lastFilter };
            onSetColumnFilters(newFilter);
        }
    }, [columnName, onSetColumnFilters]);

    // Promote the draft to applied: persist it and hand it to the parent, which is what
    // triggers the back-end fetch. Only called on Enter, on a select change, or when the
    // field is cleared — never on a plain keystroke.
    function commit(value: string) {
        setApplied(value);
        localStorage.setItem(`runFilter.${columnName}`, value);

        // Sets columnFilters parent that holds all filters and will be sent
        // to SearchBox.tsx for back end fetch filter
        const newFilter = { [columnName]: value };
        onSetColumnFilters(newFilter);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        setTerm(value);

        // Clearing takes effect immediately — waiting for Enter to un-filter reads as a
        // broken clear button (type="search" renders a native X that fires onChange "").
        if (value === "") {
            commit("");
        }
    }

    function handleSelectChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        setTerm(value);
        commit(value);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter") {
            e.preventDefault();
            commit(term);
        }
        // Escape abandons what was typed and restores the filter actually in effect.
        if (e.key === "Escape") {
            setTerm(applied);
        }
    }

    // Leaving the field commits too. Without this, typing a term and clicking away
    // leaves the grid showing unfiltered results with no further action pending, which
    // reads as the filter being ignored. Escape already reset term to applied, so
    // abandoning a draft and then clicking away still commits nothing.
    function handleBlur() {
        if (term !== applied) {
            commit(term);
        }
    }

    const isPending = term !== applied;

    if (columnName === "status") {
        return (
            <Input
                key={column.id}
                name={columnName}
                type="select"
                className={styles.searchInput}
                style={{
                    border: term
                        ? "4px solid var(--dimensionBlueHighlight)"
                        : "",
                }}
                value={term || ""}
                onChange={handleSelectChange}
            >
                <option value="">All</option>
                {statusOptions.map((value) => {
                    return (
                        <option key={value} value={value as string}>
                            {value as string}
                        </option>
                    );
                })}
            </Input>
        );
    }

    return (
        <>
            <Input
                key={column.id}
                id={column.id}
                data-testid={column.id + "input"}
                name={columnName}
                type="search"
                className={styles.searchInput}
                value={term}
                // Stop the click to select the search input from bubbling up and triggering a sort
                onClick={(e) => e.stopPropagation()}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                // Not conditional on isPending: a placeholder only renders on an empty
                // input, and an empty input has already committed, so the pending state
                // can never be visible here. The dashed outline below carries that signal.
                placeholder={`Search`}
                // Highlight the text input background if column is being filtered, and
                // mark a typed-but-not-yet-applied draft with a dashed outline so it is
                // obvious the results on screen do not reflect what is in the box.
                style={{
                    ...(applied
                        ? {
                              backgroundColor: "var(--dimensionBlueHighlight)",
                          }
                        : {}),
                    ...(isPending
                        ? { outline: "2px dashed var(--dimensionBlueHighlight)" }
                        : {}),
                }}
            />
            <UncontrolledTooltip target={column.id}>
                Press Enter to search. Use ! to exclude: !word
            </UncontrolledTooltip>
        </>
    );
}
